import { useState } from "react";
import { hatirlatmalarData } from "@/mocks/kampanyalar";
import KuralKarti from "./components/KuralKarti";

interface Kural {
  id: number;
  ad: string;
  tetikleyici: string;
  sablon: string;
  hedefKitle: string;
  durum: string;
  sonCalisma: string;
  sonrakiCalisma: string;
  aciklama: string;
  gonderilenSayisi: number;
}

interface YeniKuralModalProps {
  onClose: () => void;
}

const TETIKLEME_TURLERI = [
  { value: "odeme_once", label: "Ödeme tarihinden önce" },
  { value: "odeme_sonra", label: "Ödeme tarihinden sonra" },
  { value: "taahhut_once", label: "Taahhüt bitişinden önce" },
  { value: "aylik_gun", label: "Aylık belirli gün" },
  { value: "yeni_kayit", label: "Yeni kayıt sonrası" },
  { value: "manuel", label: "Manuel tetikleme" },
];

const SABLON_SECENEKLERI = [
  "Ödeme Hatırlatma Şablonu",
  "Gecikme Hatırlatma Şablonu",
  "Taahhüt Yenileme Şablonu",
  "Referans İsteme Şablonu",
  "Yeni Kayıt Takip Şablonu",
  "Deneme Takip Şablonu",
];

const HEDEF_KITLE_SECENEKLERI = [
  "Aktif Öğrenciler",
  "Tüm Veliler",
  "Referans Vermeyen Veliler",
  "Ödeme Geciken Veliler",
  "Taahhüdü Bitmek Üzere Olanlar",
  "Yeni Kayıtlar",
  "Deneme Dersi Alanlar",
];

function YeniKuralModal({ onClose }: YeniKuralModalProps) {
  const [ad, setAd] = useState("");
  const [tetikleme, setTetikleme] = useState("odeme_once");
  const [gunSayisi, setGunSayisi] = useState("1");
  const [sablon, setSablon] = useState(SABLON_SECENEKLERI[0]);
  const [hedefKitle, setHedefKitle] = useState(HEDEF_KITLE_SECENEKLERI[0]);
  const [aktif, setAktif] = useState(true);
  const [saved, setSaved] = useState(false);

  const showGunSayisi = tetikleme !== "manuel" && tetikleme !== "aylik_gun";
  const showAylikGun = tetikleme === "aylik_gun";

  const handleSave = () => {
    if (!ad.trim()) return;
    setSaved(true);
    setTimeout(() => { setSaved(false); onClose(); }, 1800);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !saved && onClose()} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-2xl my-4">
        {/* Header */}
        <div className="bg-[#0B1120] px-6 py-5 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-[#25D366]/20 flex items-center justify-center">
              <i className="ri-notification-3-line text-[#25D366] text-sm"></i>
            </div>
            <h3 className="text-white font-bold text-sm">Yeni Hatırlatma Kuralı Oluştur</h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-gray-400 hover:text-white cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {saved ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-checkbox-circle-line text-emerald-600 text-2xl"></i>
            </div>
            <p className="font-semibold text-gray-800">Kural oluşturuldu!</p>
            <p className="text-xs text-gray-500 mt-1">Yeni hatırlatma kuralı aktif edildi.</p>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {/* Kural adı */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Kural Adı <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={ad}
                onChange={e => setAd(e.target.value)}
                placeholder="örn. Ödeme günü 1 gün önce hatırlatma"
                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#25D366]/30 focus:border-[#25D366]"
              />
            </div>

            {/* Tetikleme türü */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Tetikleme Türü</label>
              <div className="grid grid-cols-2 gap-2">
                {TETIKLEME_TURLERI.map(t => (
                  <button
                    key={t.value}
                    onClick={() => setTetikleme(t.value)}
                    className={`px-3 py-2.5 rounded-xl text-xs font-medium text-left cursor-pointer border-2 transition-all
                      ${tetikleme === t.value
                        ? "border-[#25D366] bg-[#25D366]/5 text-[#128C4A]"
                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                      }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${tetikleme === t.value ? "border-[#25D366] bg-[#25D366]" : "border-gray-300"}`}>
                        {tetikleme === t.value && <i className="ri-check-line text-white text-[8px]"></i>}
                      </div>
                      {t.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Gün sayısı */}
            {(showGunSayisi || showAylikGun) && (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  {showAylikGun ? "Ayın Kaçıncı Günü?" : "Gün Sayısı"}
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={gunSayisi}
                    onChange={e => setGunSayisi(e.target.value)}
                    min={1}
                    max={showAylikGun ? 28 : 90}
                    className="w-28 px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#25D366]/30 focus:border-[#25D366]"
                  />
                  <span className="text-xs text-gray-500">
                    {showAylikGun
                      ? `Her ayın ${gunSayisi}. günü tetiklenir`
                      : tetikleme.includes("once")
                        ? `tarihinden ${gunSayisi} gün önce tetiklenir`
                        : `tarihinden ${gunSayisi} gün sonra tetiklenir`
                    }
                  </span>
                </div>
              </div>
            )}

            {/* Mesaj şablonu */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Mesaj Şablonu</label>
              <div className="relative">
                <select
                  value={sablon}
                  onChange={e => setSablon(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#25D366]/30 focus:border-[#25D366] appearance-none bg-white cursor-pointer"
                >
                  {SABLON_SECENEKLERI.map(s => <option key={s}>{s}</option>)}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-sm">
                  <i className="ri-arrow-down-s-line"></i>
                </div>
              </div>
            </div>

            {/* Hedef kitle */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Hedef Kitle</label>
              <div className="relative">
                <select
                  value={hedefKitle}
                  onChange={e => setHedefKitle(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#25D366]/30 focus:border-[#25D366] appearance-none bg-white cursor-pointer"
                >
                  {HEDEF_KITLE_SECENEKLERI.map(h => <option key={h}>{h}</option>)}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-sm">
                  <i className="ri-arrow-down-s-line"></i>
                </div>
              </div>
            </div>

            {/* Aktif / Pasif */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="text-sm font-semibold text-gray-800">Kural Durumu</p>
                <p className="text-xs text-gray-500">{aktif ? "Kural oluşturulduktan hemen sonra aktif olacak" : "Kural oluşturulacak ama çalışmayacak"}</p>
              </div>
              <button
                onClick={() => setAktif(!aktif)}
                className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${aktif ? "bg-[#25D366]" : "bg-gray-300"}`}
              >
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${aktif ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 text-sm border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                disabled={!ad.trim()}
                className="flex-1 py-2.5 text-sm bg-[#0B1120] text-white rounded-xl hover:bg-[#1a2a4a] cursor-pointer font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Kuralı Oluştur
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function HatirlatmalarPage() {
  const [kurallar, setKurallar] = useState<Kural[]>(hatirlatmalarData);
  const [showYeniModal, setShowYeniModal] = useState(false);

  const handleToggle = (id: number) => {
    setKurallar(prev =>
      prev.map(k =>
        k.id === id
          ? { ...k, durum: k.durum === "Aktif" ? "Pasif" : "Aktif", sonrakiCalisma: k.durum === "Aktif" ? "-" : "Hesaplanıyor..." }
          : k
      )
    );
  };

  const aktifCount = kurallar.filter(k => k.durum === "Aktif").length;
  const pasifCount = kurallar.filter(k => k.durum === "Pasif").length;
  const toplamGonderilen = kurallar.reduce((acc, k) => acc + k.gonderilenSayisi, 0);

  return (
    <div className="p-4 md:p-6 xl:p-8 space-y-6 max-w-[1600px]">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Otomatik Hatırlatmalar</h2>
          <p className="text-sm text-gray-500 mt-0.5">Ödeme, taahhüt ve referans hatırlatma kurallarınızı yönetin</p>
        </div>
        <button
          onClick={() => setShowYeniModal(true)}
          className="px-5 py-2.5 text-sm bg-[#0B1120] text-white rounded-xl hover:bg-[#1a2a4a] cursor-pointer whitespace-nowrap flex items-center gap-2 transition-colors font-medium"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line text-base"></i>
          </div>
          Yeni Hatırlatma Kuralı Oluştur
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Toplam Kural", value: kurallar.length, icon: "ri-notification-3-line", color: "bg-[#0B1120] text-white" },
          { label: "Aktif Kural", value: aktifCount, icon: "ri-play-circle-line", color: "bg-[#25D366] text-white" },
          { label: "Pasif Kural", value: pasifCount, icon: "ri-pause-circle-line", color: "bg-gray-400 text-white" },
          { label: "Toplam Gönderim", value: toplamGonderilen, icon: "ri-send-plane-line", color: "bg-sky-500 text-white" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.color} flex-shrink-0`}>
              <i className={`${s.icon} text-base`}></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* System status banner */}
      <div className="bg-[#0B1120] rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#25D366]/20 border border-[#25D366]/30 flex items-center justify-center">
            <div className="w-5 h-5 flex items-center justify-center text-[#25D366]">
              <i className="ri-robot-line text-base"></i>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-white font-semibold text-sm">Aktif Hatırlatma Sistemi</p>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#25D366]/20 text-[#25D366] rounded-full text-[11px] font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-[#25D366] animate-pulse"></span>
                Çalışıyor
              </span>
            </div>
            <p className="text-gray-400 text-xs mt-0.5">Sistem her 6 dakikada bir sıradaki mesajı gönderecek şekilde hazırlanmıştır.</p>
          </div>
        </div>
        <div className="sm:ml-auto text-right">
          <p className="text-gray-400 text-xs">Aktif kurallar</p>
          <p className="text-white font-bold text-lg">{aktifCount} / {kurallar.length}</p>
        </div>
      </div>

      {/* Rules grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {kurallar.map(k => (
          <KuralKarti
            key={k.id}
            kural={k}
            onToggle={() => handleToggle(k.id)}
            onDetay={() => {}}
            onDuzenle={() => {}}
          />
        ))}
      </div>

      {showYeniModal && <YeniKuralModal onClose={() => setShowYeniModal(false)} />}
    </div>
  );
}